import {
  MembershipActivityFilter,
  MembershipActivityFragment,
  MembershipActivityTypename,
  Point_Change_Source_Type,
} from '@occc/graphql';
import { getPointChangeSourceName } from '@occc-app/routes/Transactions/PointChangeView/utils';

import { pointChangeTypeFilterOptions } from './membership-activity.constants';
import { MembershipActivityType, PointChangeTypeFilterValue } from './membership-activity.types';

export const isPointChangeTypeFilterValue = (value: string): value is PointChangeTypeFilterValue =>
  pointChangeTypeFilterOptions.some((option) => option.value === value);

export const getPointChangeSourceTypeFilter = (
  type: MembershipActivityType,
  pointChangeType: PointChangeTypeFilterValue,
): Pick<
  MembershipActivityFilter,
  'pointChangeSourceTypeExcludes' | 'pointChangeSourceTypeIncludes'
> => {
  if (type !== MembershipActivityTypename.PointChange) {
    return { pointChangeSourceTypeExcludes: [Point_Change_Source_Type.Transaction] };
  }

  if (pointChangeType === 'any') {
    return {};
  }

  const sourceTypes = pointChangeTypeFilterOptions.find(
    (option) => option.value === pointChangeType,
  )?.sourceTypes;

  if (!sourceTypes?.length) {
    return {};
  }

  return { pointChangeSourceTypeIncludes: sourceTypes };
};

export const getSource = (activity: MembershipActivityFragment) => {
  if (activity.event.__typename === 'ActivateTransaction') {
    return 'Transaction';
  }

  return (
    getPointChangeSourceName({
      type: activity.event.type,
      sourceTypename: activity.event.source?.__typename,
    }) || null
  );
};

export const getOfferName = (activity: MembershipActivityFragment): string | null => {
  if (activity.event.__typename !== 'PointChange') {
    return null;
  }

  const { offer } = activity.event;

  return offer ? offer.marketingContents[0]?.title || offer.name : null;
};

const TYPOGRAPHIC_MINUS = '\u2212';

export const formatPointChange = (amount: number): string => {
  if (amount < 0) {
    return `${TYPOGRAPHIC_MINUS}${Math.abs(amount)}`;
  }

  if (amount > 0) {
    return `+${amount}`;
  }

  return '0';
};

export const getPointChangeAmount = (activity: MembershipActivityFragment): number => {
  if (activity.event.__typename === 'PointChange') {
    return activity.event.quantity;
  }
  if (activity.event.__typename === 'ActivateTransaction') {
    if (activity.event.totalPointsEarned > 0) {
      return activity.event.totalPointsEarned;
    }
    if (activity.event.totalPointsSpent > 0) {
      return -activity.event.totalPointsSpent;
    }
    return 0;
  }

  return 0;
};

export const getLocation = (activity: MembershipActivityFragment) => {
  if (activity.event.__typename === 'ActivateTransaction') {
    return activity.event.location;
  }

  if (
    activity.event.__typename === 'PointChange' &&
    activity.event.source?.__typename === 'ActivateTransaction'
  ) {
    return activity.event.source.location;
  }

  return null;
};

export const getTotalGallons = (activity: MembershipActivityFragment): number | null => {
  if (activity.event.__typename === 'ActivateTransaction') {
    return activity.event.totalGallons ?? null;
  }

  return null;
};

export const getTotalAmount = (activity: MembershipActivityFragment) => {
  if (activity.event.__typename === 'ActivateTransaction') {
    return activity.event.totalAmount;
  }

  return null;
};
